import { NavLink as RouterNavLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  LayoutDashboard, Briefcase, Users, Building2, Bell,
  ChevronDown, ChevronsLeft, ChevronsRight, Menu, Plus,
  Zap, Clock, Shield, LifeBuoy
} from 'lucide-react';
import { initSupportDiagnosticsCapture } from '@/lib/supportDiagnostics';
import { useIsSudo } from '@/hooks/useIsSudo';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import NotificationCenter from '@/components/NotificationCenter';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNotifications } from '@/hooks/useNotifications';
import { useApplicationListNotificationSync } from '@/hooks/useApplicationListNotificationSync';
import { useApplicationLiveSync } from '@/hooks/useApplicationLiveSync';
import { useBulkProcessBannerSync } from '@/hooks/useBulkProcessBannerSync';
import { api } from '@/services/api';
import TrialBanner from '@/components/billing/TrialBanner';
import TrialExpiredBanner from '@/components/billing/TrialExpiredBanner';
import UpgradeWallModal from '@/components/billing/UpgradeWallModal';
import UpgradeInsteadOfCreateModal from '@/components/billing/UpgradeInsteadOfCreateModal';
import { AgencyContactModal } from '@/components/billing/AgencyContactModal';
import { AGENCY_SELF_SERVE_ENABLED } from '@/lib/productFlags';
import { isAgencyOwnerForProduct } from '@/lib/agencyOwnerDetection';
import PostUpgradeBanner from '@/components/billing/PostUpgradeBanner';
import { useTrialWriteGuard } from '@/hooks/useTrialWriteAccess';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useGlobalLoadingActive } from '@/stores/loadingStore';
import { brandLoadingBarFillClass, brandLoadingBarTrackClass } from '@/components/shared/TopLoadingBar';
import { useViewportLockedListPage } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CreditCard } from 'lucide-react';

interface AddCompanySeatConfirmModalProps {
  open: boolean;
  loading: boolean;
  quote: {
    currentQuantity: number;
    newQuantity: number;
    pricePerSeat: number;
    proratedNow: number;
    newMonthlyTotal: number;
    currency: string;
  } | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * v3.2 polish (21/05/26 ~22:05) — pay-as-you-grow confirm modal.
 *
 * Shown after "Aggiungi Azienda" click for agency owners. Lists the
 * prorated charge that will hit Stripe NOW + the new monthly total
 * (current_seats + 1) × per-seat-price. On confirm we let the user
 * proceed to /onboarding where the actual charge happens at form
 * submit (CompanyService.createCompany bumps the Stripe quantity).
 */
function AddCompanySeatConfirmModal({
  open, loading, quote, onConfirm, onCancel,
}: AddCompanySeatConfirmModalProps) {
  const { t, language } = useLanguage();
  const isIt = language === 'it';

  const fmt = (n: number, currency: string) =>
    new Intl.NumberFormat(isIt ? 'it-IT' : 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 2,
    }).format(n);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-full bg-brand-primary/10 flex items-center justify-center mb-2">
            <CreditCard className="h-6 w-6 text-brand-primary" />
          </div>
          <DialogTitle className="text-center">
            {t('addCompany.seatConfirm.title')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('addCompany.seatConfirm.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {loading || !quote ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 px-4 py-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('addCompany.seatConfirm.currentSeats')}
                </span>
                <span className="font-medium tabular-nums">{quote.currentQuantity}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t('addCompany.seatConfirm.newSeats')}
                </span>
                <span className="font-medium tabular-nums">{quote.newQuantity}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2">
                <span className="text-muted-foreground">
                  {t('addCompany.seatConfirm.pricePerSeat')}
                </span>
                <span className="font-medium tabular-nums">{fmt(quote.pricePerSeat, quote.currency)}</span>
              </div>
            </div>

            <div className="rounded-md border border-brand-primary/20 bg-brand-primary/5 px-4 py-3 space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t('addCompany.seatConfirm.proratedNow')}
                </span>
                <span className="text-2xl font-bold text-brand-primary tabular-nums">
                  {fmt(quote.proratedNow, quote.currency)}
                </span>
              </div>
              <div className="flex justify-between items-baseline text-xs text-muted-foreground">
                <span>{t('addCompany.seatConfirm.newMonthlyTotal')}</span>
                <span className="tabular-nums">
                  {fmt(quote.newMonthlyTotal, quote.currency)}
                  /{isIt ? 'mese' : 'mo'}
                </span>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground text-center px-2">
              {t('addCompany.seatConfirm.fineprint')}
            </p>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            className="bg-brand-primary hover:bg-brand-primary-mid text-white"
            disabled={loading || !quote}
            onClick={onConfirm}
          >
            {t('addCompany.seatConfirm.cta')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SidebarNav = ({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) => {
  const { user, selectedCompany, setSelectedCompany } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    initSupportDiagnosticsCapture();
  }, []);
  // admin SUPER-MVP M1 (27/05/26): voce "Backoffice" condizionale per i sudo.
  // L'hook fa force-refresh del JWT al primo mount, così un claim
  // appena grantato è visibile senza dover fare sign-out + sign-in.
  const { isSudo, isLoading: sudoLoading } = useIsSudo();
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [eligibilityChecking, setEligibilityChecking] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    existingCompany: { id: string; name: string; planId: string | null } | null;
    reason?: 'sme_admin_exists' | 'agency_client_cap_reached';
    capInfo?: { used: number; cap: number; agencyPlanId: string };
    suggestedActions?: Array<'upgrade_sme' | 'upgrade_agency' | 'convert_to_agency' | 'contact_support'>;
  }>({ open: false, existingCompany: null });
  const [agencyContactOpen, setAgencyContactOpen] = useState(false);

  // v3.2 polish (21/05/26 ~22:05) — pay-as-you-grow confirm modal.
  // Shown to agency owners BEFORE navigating to /onboarding so they see
  // the prorated charge they're about to incur. Without this they'd
  // hit Stripe at form-submit time with no heads-up.
  const [seatConfirm, setSeatConfirm] = useState<{
    open: boolean;
    loading: boolean;
    quote: {
      currentQuantity: number;
      newQuantity: number;
      pricePerSeat: number;
      proratedNow: number;
      newMonthlyTotal: number;
      currency: string;
    } | null;
  }>({ open: false, loading: false, quote: null });

  const handleAddCompanyClick = async () => {
    if (eligibilityChecking) return;

    // Agency self-serve pause: SME users contact sales instead of convert-to-agency flow.
    if (!AGENCY_SELF_SERVE_ENABLED && !isAgencyOwnerForProduct(user)) {
      setAgencyContactOpen(true);
      return;
    }

    setEligibilityChecking(true);
    try {
      const verdict = await api.auth.getOnboardingEligibility();
      if (verdict.canCreate) {
        // v3.2 polish: if user is an agency owner, show the pay-as-you-grow
        // confirm modal first. The BE preview API computes the prorated
        // charge (Stripe-accurate, not a UI guess). On confirm we navigate
        // to /onboarding where the form submit triggers the real bump.
        if (isAgencyOwnerForProduct(user)) {
          setSeatConfirm({ open: true, loading: true, quote: null });
          try {
            const quote = await api.billing.previewAddCompanyForAgency();
            setSeatConfirm({
              open: true,
              loading: false,
              quote: {
                currentQuantity: quote.currentQuantity,
                newQuantity: quote.newQuantity,
                pricePerSeat: quote.pricePerSeat,
                proratedNow: quote.proratedNow,
                newMonthlyTotal: quote.newMonthlyTotal,
                currency: quote.currency,
              },
            });
          } catch (err) {
            // If preview fails (e.g. no Stripe sub on file yet), fall back
            // to opening the form directly — the BE create-company guard
            // will catch any billing issue with a clear error.
            console.error('[AppLayout] agency seat preview failed, falling through', err);
            setSeatConfirm({ open: false, loading: false, quote: null });
            navigate('/onboarding');
          }
          return;
        }
        navigate('/onboarding');
        return;
      }
      // BE drift guard: agency UI but eligibility still returns SME cap (stale
      // users/{uid}.billing.planId). Route to pay-as-you-grow seat confirm.
      if (
        verdict.reason === 'sme_admin_exists' &&
        isAgencyOwnerForProduct(user)
      ) {
        setSeatConfirm({ open: true, loading: true, quote: null });
        try {
          const quote = await api.billing.previewAddCompanyForAgency();
          setSeatConfirm({
            open: true,
            loading: false,
            quote: {
              currentQuantity: quote.currentQuantity,
              newQuantity: quote.newQuantity,
              pricePerSeat: quote.pricePerSeat,
              proratedNow: quote.proratedNow,
              newMonthlyTotal: quote.newMonthlyTotal,
              currency: quote.currency,
            },
          });
        } catch (err) {
          console.error('[AppLayout] agency seat preview (eligibility drift guard)', err);
          setSeatConfirm({ open: false, loading: false, quote: null });
          navigate('/onboarding');
        }
        return;
      }
      if (verdict.reason === 'agency_client_cap_reached' && verdict.capInfo) {
        setUpgradeModal({
          open: true,
          existingCompany: verdict.existingCompany ?? null,
          reason: 'agency_client_cap_reached',
          capInfo: verdict.capInfo,
          suggestedActions: verdict.suggestedActions,
        });
      } else if (verdict.existingCompany) {
        setUpgradeModal({
          open: true,
          existingCompany: verdict.existingCompany,
          reason: (verdict.reason as 'sme_admin_exists') ?? 'sme_admin_exists',
          suggestedActions: verdict.suggestedActions,
        });
      } else {
        navigate('/onboarding');
      }
    } catch {
      // Fallback: let the form open; BE will return 409 if needed.
      navigate('/onboarding');
    } finally {
      setEligibilityChecking(false);
    }
  };

  const handleSeatConfirm = () => {
    setSeatConfirm({ open: false, loading: false, quote: null });
    navigate('/onboarding');
  };
  const handleSeatCancel = () => {
    setSeatConfirm({ open: false, loading: false, quote: null });
  };

  useEffect(() => {
    const membershipList = Object.values(user?.memberships ?? {});
    if (!membershipList.length) return;

    const missingCompanies = membershipList.filter(m => !(m.company?.name || '').trim());
    if (!missingCompanies.length) return;

    let cancelled = false;

    Promise.all(
      missingCompanies.map(async (membership) => {
        const full = await api.companies.get(membership.companyId);
        return full?.name ? [membership.companyId, full.name] as const : null;
      })
    ).then((results) => {
      if (cancelled) return;
      const next = Object.fromEntries(results.filter(Boolean) as Array<readonly [string, string]>);
      if (Object.keys(next).length) {
        setCompanyNames(prev => ({ ...prev, ...next }));
      }
    }).catch(() => {
      // non-blocking
    });

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(user?.memberships)]);

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/job-positions', icon: Briefcase, label: t('nav.positions') },
    { to: '/applications', icon: Users, label: t('nav.applications') },
    { to: '/settings', icon: Building2, label: t('nav.company') },
  ];

  const membershipList = Object.values(user?.memberships ?? {});
  const membershipIds = membershipList.map(m => m.companyId);
  const allCompanies = selectedCompany && !membershipIds.includes(selectedCompany.id)
    ? [{ companyId: selectedCompany.id, company: selectedCompany }, ...membershipList]
    : membershipList;

  const handleSelectCompany = (company: NonNullable<typeof selectedCompany>) => {
    void setSelectedCompany(company);
    setCompanySwitcherOpen(false);
    navigate('/dashboard');
  };

  return (
    <div className={`flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}>
      {/* Company header — Sprint 7 (S7.B): clicking the company name now
          navigates to /settings (Company tab is the default), which is the
          de-facto "Company detail" page in HiroMatch. There is no separate
          /companies/:id route; the settings tabs are the single source of
          truth for company metadata. */}
      <RouterNavLink
        to="/settings"
        className={cn(
          'h-14 flex items-center gap-2 border-b border-sidebar-border shrink-0 hover:bg-sidebar-accent/30 transition-colors group',
          collapsed ? 'justify-center px-2' : 'px-3',
        )}
        aria-label={t('nav.companySettings')}
        title={selectedCompany?.name || ''}
      >
        <svg
          viewBox="0 0 360 320"
          xmlns="http://www.w3.org/2000/svg"
          className="h-7 w-7 shrink-0 transition-transform group-hover:scale-105"
          aria-label="HiroMatch"
        >
          <defs>
            <clipPath id="hm-sidebar-clip">
              <circle cx="225" cy="160" r="90" />
            </clipPath>
          </defs>
          <circle cx="135" cy="160" r="88" fill="#7C3AED" opacity="0.9" />
          <circle cx="225" cy="160" r="90" fill="#C4B5FD" opacity="0.82" />
          <circle cx="135" cy="160" r="88" fill="#9333EA" opacity="0.3" clipPath="url(#hm-sidebar-clip)" />
        </svg>
        {!collapsed && (
          <span className="font-semibold text-sm text-sidebar-foreground truncate">
            {selectedCompany?.name || companyNames[selectedCompany?.id || ''] || 'hiromatch'}
          </span>
        )}
      </RouterNavLink>

      {/* Nav items */}
      <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <RouterNavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </RouterNavLink>
        ))}

        {/* admin SUPER-MVP M1 (27/05/26): Backoffice entry visibile SOLO al sudo.
            Posizionata DOPO le voci recruiter standard, con separatore visivo
            e badge "Admin" per segnalare zona privilegiata. SudoGuard si occupa
            comunque di proteggere la rotta lato server, questo è solo l'entry
            point visibile per chi ha già il claim. */}
        {!sudoLoading && isSudo && (
          <>
            <div className="mx-2.5 my-2 border-t border-sidebar-border/50" />
            <RouterNavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                } ${collapsed ? 'justify-center' : ''}`
              }
              title={collapsed ? t('nav.adminBackoffice') : undefined}
            >
              <Shield className="h-4 w-4 shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1">{t('nav.adminBackoffice')}</span>
                  <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-red-500/15 text-red-600 dark:text-red-400 leading-none">
                    Admin
                  </span>
                </>
              )}
            </RouterNavLink>
          </>
        )}
      </nav>

      {user && (
        <div className={cn('shrink-0', collapsed ? 'px-1 py-1' : 'px-2 py-1.5')}>
          <button
            type="button"
            onClick={() => navigate('/support')}
            className={cn(
              'w-full flex items-center justify-center gap-2 rounded-md text-xs text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors',
              collapsed ? 'py-2' : 'px-2.5 py-1.5',
            )}
            title={collapsed ? t('nav.reportIssue') : undefined}
          >
            <LifeBuoy className="h-3.5 w-3.5 shrink-0" />
            {!collapsed && <span className="truncate text-center">{t('nav.reportIssue')}</span>}
          </button>
        </div>
      )}

      {/* Collapse toggle */}
      <div className="px-2 py-1">
        <button onClick={onToggle} className="w-full flex items-center justify-center py-1.5 rounded-md text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors">
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Divider — full sidebar width */}
      <div className="border-t border-sidebar-border shrink-0" />

      {/* Company switcher — inline panel expands upward inside the sidebar */}
      {user && (
        <div className={cn('shrink-0', companySwitcherOpen ? 'pb-2 pt-0' : 'px-2 py-2')}>
          {collapsed ? (
            <div className="flex items-center justify-center py-1.5">
              <Building2 className="h-4 w-4 text-sidebar-foreground/50" />
            </div>
          ) : (
            <div className="flex flex-col">
              {companySwitcherOpen && (
                <div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-sidebar-border">
                    {allCompanies.map(m => {
                      const displayCompany = selectedCompany?.id === m.companyId ? selectedCompany : m.company;
                      const displayName = displayCompany.name || companyNames[m.companyId] || '—';
                      const isSelected = selectedCompany?.id === m.companyId;
                      return (
                        <button
                          key={m.companyId}
                          type="button"
                          onClick={() => handleSelectCompany(displayCompany)}
                          className={cn(
                            'w-full flex items-center gap-2 px-4 py-2 text-xs text-left transition-colors',
                            isSelected
                              ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                              : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                          )}
                        >
                          <Building2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{displayName}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="border-t border-sidebar-border">
                    <button
                      type="button"
                      onClick={() => { void handleAddCompanyClick(); setCompanySwitcherOpen(false); }}
                      disabled={eligibilityChecking}
                      className="w-full flex items-center gap-2 px-4 py-2 text-xs text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors disabled:opacity-50"
                    >
                      <Plus className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{t('nav.addCompany')}</span>
                    </button>
                  </div>
                </div>
              )}
              <div className="px-2">
                <button
                  type="button"
                  onClick={() => setCompanySwitcherOpen(open => !open)}
                  aria-expanded={companySwitcherOpen}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs hover:bg-sidebar-accent/50 transition-colors"
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/70" />
                  <span className="flex-1 text-left truncate text-sidebar-foreground font-medium">
                    {selectedCompany?.name || companyNames[selectedCompany?.id || ''] || '—'}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 text-sidebar-foreground/50 transition-transform duration-200',
                      companySwitcherOpen && 'rotate-180',
                    )}
                  />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User section — click goes straight to profile account tab */}
      <div className="border-t border-sidebar-border px-2 py-2 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/profile?tab=account')}
          className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-sidebar-accent/50 transition-colors ${collapsed ? 'justify-center' : ''}`}
          aria-label={t('nav.profile')}
        >
          <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0 overflow-hidden">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName || user.email}
                className="h-full w-full object-cover"
              />
            ) : (
              user?.displayName?.[0] || 'U'
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.displayName}</p>
              <p className="text-xs text-sidebar-foreground/50 truncate">{user?.email}</p>
            </div>
          )}
        </button>
      </div>
      <UpgradeInsteadOfCreateModal
        open={upgradeModal.open}
        existingCompany={upgradeModal.existingCompany}
        reason={upgradeModal.reason}
        capInfo={upgradeModal.capInfo}
        suggestedActions={upgradeModal.suggestedActions}
        onClose={() => setUpgradeModal({ open: false, existingCompany: null })}
      />

      <AgencyContactModal
        open={agencyContactOpen}
        onClose={() => setAgencyContactOpen(false)}
        context="add_company"
      />

      <AddCompanySeatConfirmModal
        open={seatConfirm.open}
        loading={seatConfirm.loading}
        quote={seatConfirm.quote}
        onConfirm={handleSeatConfirm}
        onCancel={handleSeatCancel}
      />
    </div>
  );
};

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { selectedCompany } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notif = useNotifications();
  const { guardWrite } = useTrialWriteGuard();
  useBulkProcessBannerSync(selectedCompany?.id);
  useApplicationLiveSync(selectedCompany?.id);
  useApplicationListNotificationSync();

  // Pre-fetch billing status at app level so it's warm when user navigates
  // to Job Positions or other pages that need cap checks. The React Query
  // cache is shared, so subsequent useBillingStatus() calls get instant data.
  useBillingStatus();

  const pageTitles: Record<string, string> = {
    '/dashboard': t('nav.dashboard'),
    '/job-positions': t('nav.positions'),
    '/applications': t('nav.applications'),
    '/settings': t('nav.company'),
    '/profile': t('nav.profile'),
  };

  const pageTitle = Object.entries(pageTitles).find(([path]) => location.pathname.startsWith(path))?.[1] || t('nav.dashboard');

  const isNarrowContentPage =
    location.pathname.startsWith('/settings') || location.pathname.startsWith('/profile');

  const lockMainScroll = useViewportLockedListPage();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex w-full bg-background">
      <Helmet>
        <title>HiroMatch</title>
      </Helmet>
      <div className="hidden md:flex shrink-0 sticky top-0 h-screen">
        <SidebarNav collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-60">
          <SheetHeader className="sr-only">
            <SheetTitle>{t('nav.menu')}</SheetTitle>
            <SheetDescription>{t('nav.mainNavigation')}</SheetDescription>
          </SheetHeader>
          <SidebarNav collapsed={false} onToggle={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <NotificationCenter
        open={notifOpen}
        onOpenChange={setNotifOpen}
      />

      <div className="flex-1 flex flex-col min-w-0 min-h-0 h-svh overflow-hidden">
        <header className="h-14 border-b border-border flex items-center justify-between px-4 md:px-6 bg-card shrink-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button className="md:hidden shrink-0" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5 text-muted-foreground" />
            </button>
            <h1 className="text-base font-semibold truncate">{pageTitle}</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Zap className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => {
                  if (!guardWrite()) return;
                  navigate('/job-positions?action=new');
                }}>
                  <Plus className="h-3.5 w-3.5 mr-2" /> {t('nav.newPosition')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/applications?status=AWAITING_DECISION')}>
                  <Clock className="h-3.5 w-3.5 mr-2" /> {t('nav.awaitingDecision')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" className="relative" onClick={() => setNotifOpen(true)}>
              <Bell className="h-4.5 w-4.5 text-muted-foreground" />
              {notif.unreadCount > 0 && (
                <Badge className="absolute -top-0.5 -right-0.5 h-4 min-w-4 flex items-center justify-center text-[10px] px-1 bg-destructive text-destructive-foreground border-0">
                  {notif.unreadBadgeLabel}
                </Badge>
              )}
            </Button>
          </div>
        </header>
        <GlobalLoadingBar />
        <TrialExpiredBanner />
        <TrialBanner />

        <main
          className={cn(
            'flex-1 flex flex-col min-h-0 min-w-0 relative [scrollbar-gutter:stable]',
            lockMainScroll ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden',
          )}
        >
          <div
            className={cn(
              'mx-auto w-full min-w-0 flex flex-col gap-4',
              isNarrowContentPage ? 'max-w-4xl' : 'max-w-screen-2xl',
              lockMainScroll
                ? 'flex-1 min-h-0 overflow-hidden p-4 md:p-6'
                : 'px-4 pt-4 pb-10 md:px-6 md:pt-6 md:pb-12',
            )}
          >
            <div className={cn(lockMainScroll && 'shrink-0')}>
              <PostUpgradeBanner />
            </div>
            <div
              className={cn(
                'flex flex-col',
                lockMainScroll && 'flex-1 min-h-0 overflow-hidden',
              )}
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      <UpgradeWallModal />
    </div>
  );
};

/**
 * Global loading indicator — full width of the content column (not the sidebar),
 * pinned directly under the page header (above trial banner and scrollable main).
 */
function GlobalLoadingBar(): JSX.Element {
  const active = useGlobalLoadingActive();
  return (
    <div
      className={cn(
        'relative z-40 w-full shrink-0 pointer-events-none transition-all duration-200',
        active ? 'h-0.5 opacity-100' : 'h-0 opacity-0',
      )}
      aria-hidden={!active}
    >
      <div
        className={brandLoadingBarTrackClass()}
        role="progressbar"
        aria-busy={active}
        aria-label="Loading"
      >
        {active && <div className={brandLoadingBarFillClass} />}
      </div>
    </div>
  );
}

export default AppLayout;
